// src/App.jsx — main shell, no business logic
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from './api/client';

import StructuredDS from './learning/datascience/StructuredDS';
import ExcelData from './learning/datascience/ExcelData';
import FeatureEng from './learning/ml/FeatureEng';
import RegressionLab from './learning/ml/RegressionLab';
import ClassificationLab from './learning/ml/ClassificationLab';
import ClusteringLab from './learning/ml/ClusteringLab';
import EvaluationLab from './learning/ml/EvaluationLab';
import ImageData from './learning/datascience/ImageData';
import AudioData from './learning/datascience/AudioData';
import DocumentData from './learning/datascience/DocumentData';
import VideoData from './learning/datascience/VideoData';
import NeuralNetworks from './learning/dl/NeuralNetworks';
import ActivationsLab from './learning/dl/ActivationsLab';
import BackpropLab from './learning/dl/BackpropLab';
import RegularizationLab from './learning/dl/RegularizationLab';
import CNNLab from './learning/dl/CNNLab';
import RNNLab from './learning/dl/RNNLab';
import AttentionLab from './learning/dl/AttentionLab';
import TrainingLab from './learning/dl/TrainingLab';

import UserProfile from './components/UserProfile';

import ProfilePage from './components/ProfilePage.jsx';

import ColorSpaceLab from './learning/cv/ColorSpaceLab';
import EdgeLab from './learning/cv/EdgeLab';
import FeaturesLab from './learning/cv/FeaturesLab';
import FiltersLab from './learning/cv/FiltersLab';
import FrequencyLab from './learning/cv/FrequencyLab';
import MorphologyLab from './learning/cv/MorphologyLab';
import NeuralCVLab from './learning/cv/NeuralCVLab';
import SegmentationLab from './learning/cv/SegmentationLab';

import TokenizationLab from './learning/nlp/TokenizationLab.jsx';
import TextStatsLab from './learning/nlp/TextStatsLab.jsx';
import TFIDFLab from './learning/nlp/TFIDFLab.jsx';
import SentimentLab from './learning/nlp/SentimentLab.jsx';
import NERLab from './learning/nlp/NERLab.jsx';

import LangModelLab from './learning/nlp/LangModelLab.jsx';
import EmbeddingsLab from './learning/nlp/EmbeddingsLab.jsx';
import TransformersLab from './learning/nlp/TransformersLab.jsx';

import VAELab from './learning/genai/VAELab.jsx';
import GANLab from './learning/genai/GANLab.jsx';
import LLMLab from './learning/genai/LLMLab.jsx';
import DiffusionLab from './learning/genai/DiffusionLab.jsx';
import EvalLab from './learning/genai/EvalLab.jsx';
import FineTuningLab from './learning/genai/FineTuningLab.jsx';

import QLearningLab from './learning/rl/QLearningLab.jsx';
import PolicyGradientLab from './learning/rl/PolicyGradientLab.jsx';
import DeepRLLab from './learning/rl/DeepRLLab.jsx';
import EnvLab from './learning/rl/EnvLab.jsx';
import ExplorationLab from './learning/rl/ExplorationLab.jsx';


import AgentCoreLab from './learning/agentic/AgentCoreLab';
import ToolUseLab from './learning/agentic/ToolUseLab';
import MultiAgentLab from './learning/agentic/MultiAgentLab';
import RAGLab from './learning/agentic/RAGLab';
import PlanningLab from './learning/agentic/PlanningLab';
import MemoryLab from './learning/agentic/MemoryLab';

import QubitLab from './learning/quantum/QubitLab';
import QuantumCircuits from './learning/quantum/QuantumCircuits';
import QMLLab from './learning/quantum/QMLLab';
import QuantumOptimizer from './learning/quantum/QuantumOptimizer';
import HybridAI from './learning/quantum/HybridAI';
import QuantumFuture from './learning/quantum/QuantumFuture';

import BrainSection from './learning/edgeai/BrainSection.jsx';
import TransformersSection from './learning/edgeai/TransformersSection.jsx';
import TensorFlowJSSection from './learning/edgeai/TensorFlowSection.jsx';
import MediaPipeSection from './learning/edgeai/MediaPipeSection.jsx';
import ONNXSection from './learning/edgeai/ONNXSection.jsx';
import FaceAPISection from './learning/edgeai/FaceAPISection.jsx';
import ML5Section from './learning/edgeai/ML5Section.jsx';

import HomeHub from './workspace/HomeHub';
import LabsHub from './workspace/LabsHub';
import DemosHub from './workspace/DemosHub';
import AutoEDAHub from './workspace/AutoEDAHub';
import DocumentAnalysisHub from './workspace/DocumentAnalysisHub';
import MLExperimentsHub from './workspace/MLExperimentsHub';
import DLExperimentsHub from './workspace/DLExperimentsHub';
import LocalAgents from './workspace/LocalAgents';
import ImageInsightsHub from './workspace/ImageInsightsHub';
import AudioVideoHub from './workspace/AudioVideoHub';
import PaperAnalysisHub from './workspace/PaperAnalysisHub';
import RightPanel from './workspace/ui/RightPanel';
import WorkspaceDock from './workspace/WorkspaceDock';


import AuthModal from './components/AuthModal';
import PricingPage from './components/PricingPage';
import MembershipModal from './components/MembershipModal';
import UsageModal from './components/UsageModal';
import MarketingLandingPage from './components/MarketingLandingPage';
import PublicReportPage from './components/PublicReportPage';
import UpgradeNudge from './components/UpgradeNudge';
import ErrorBoundary from './components/ErrorBoundary';
import PaletteSwitcher from './components/PaletteSwitcher';
import { useAuth } from './context/AuthContext';
import { useChartPalette } from './context/ChartPaletteContext';
import { FEATURE_FLAGS, SITE_URL } from './config/runtime';
import { clearPendingCheckout, getPendingCheckout } from './utils/paymentRecovery';
import { initActionStore, trackAction, getUserKey, clearSession } from './utils/actionStore';
import { normalizePublicReportPath } from './utils/publicReports';

import { ChatSystem } from './learning/chat';


const SimPlaceholder = ({ label }) => (
  <div style={{ padding: '60px', textAlign: 'center', color: 'var(--t3)' }}>
    <div style={{ fontSize: '32px', marginBottom: '14px', opacity: .3 }}>⬟</div>
    <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '18px', color: 'var(--t1)', marginBottom: '8px' }}>{label}</div>
    <div style={{ fontSize: '12px' }}>Simulation module — coming soon</div>
  </div>
);

// ── Reset-password page (standalone, rendered at /reset-password?token=...) ──
function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [pw, setPw] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [done, setDone] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { setErr('Passwords do not match.'); return; }
    if (!token) { setErr('Invalid or missing reset token. Request a new reset link.'); return; }
    setBusy(true);
    try {
      await api.auth.resetPassword(token, pw);
      setDone(true);
    } catch (ex) {
      setErr(ex?.data?.error || ex?.message || 'Reset failed. The link may have expired.');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px', background: 'var(--deep)',
    border: '1px solid var(--b1)', color: 'var(--t1)', fontSize: '13px',
    fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none',
    boxSizing: 'border-box', letterSpacing: '.04em',
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--void)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '420px', background: 'var(--surface)',
        border: '1px solid var(--b1)', borderRadius: '6px', overflow: 'hidden',
      }}>
        <div style={{ padding: '24px 28px 8px', borderBottom: '1px solid var(--b0)' }}>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '20px', color: 'var(--t1)', marginBottom: '4px' }}>
            Set new password
          </div>
          <div style={{ fontSize: '11px', color: 'var(--t3)' }}>Jorvex</div>
        </div>
        <div style={{ padding: '28px' }}>
          {!done ? (
            <form onSubmit={handleSubmit}>
              {err && (
                <div style={{ padding: '10px 14px', background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', borderRadius: '3px', color: '#f87171', fontSize: '11px', marginBottom: '16px' }}>
                  {err}
                </div>
              )}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '6px' }}>NEW PASSWORD</label>
                <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="min 8 characters" style={inputStyle} autoFocus />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '6px' }}>CONFIRM PASSWORD</label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="repeat password" style={inputStyle} />
              </div>
              <button type="submit" disabled={busy} style={{
                width: '100%', padding: '13px', background: busy ? 'var(--b0)' : 'var(--cyan)',
                border: 'none', color: busy ? 'var(--t3)' : '#020508',
                fontFamily: 'JetBrains Mono', fontSize: '11px', fontWeight: 700,
                letterSpacing: '.1em', borderRadius: '3px', cursor: busy ? 'wait' : 'pointer',
              }}>
                {busy ? 'SAVING...' : 'SET NEW PASSWORD ->'}
              </button>
            </form>
          ) : (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>✓</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '16px', color: 'var(--t1)', marginBottom: '8px' }}>
                Password updated
              </div>
              <p style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '20px' }}>
                Your password has been changed. You can now sign in.
              </p>
              <a href="/" style={{
                display: 'inline-block', padding: '10px 24px',
                background: 'var(--cyan)', color: '#020508',
                fontFamily: 'JetBrains Mono', fontSize: '10px', fontWeight: 700,
                letterSpacing: '.1em', borderRadius: '3px', textDecoration: 'none',
              }}>
                GO TO APP →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const LogoutConfirmModal = ({ onCancel, onConfirm }) => (
  <div
    onClick={onCancel}
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(2,5,8,.88)',
      zIndex: 220,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      backdropFilter: 'blur(4px)',
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        maxWidth: '420px',
        background: 'var(--surface)',
        border: '1px solid var(--b1)',
        borderRadius: '6px',
        overflow: 'hidden',
        animation: 'authSlideUp .25s cubic-bezier(.16,1,.3,1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid var(--b0)' }}>
        <div>
          <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.14em', marginBottom: '4px' }}>SESSION</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '18px', color: 'var(--t1)' }}>Confirm logout</div>
        </div>
        <button onClick={onCancel} style={{ width: '34px', height: '34px', border: '1px solid var(--b0)', background: 'var(--deep)', color: 'var(--t3)', cursor: 'pointer', borderRadius: '3px', fontSize: '14px' }}>✕</button>
      </div>
      <div style={{ padding: '22px 20px 20px' }}>
        <div style={{ marginBottom: '10px', fontSize: '11px', color: 'var(--orange)', letterSpacing: '.12em' }}>ACTIVE TOKEN WILL BE CLEARED</div>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--t2)', lineHeight: 1.8 }}>
          You will be signed out of this browser session and returned to the guest state.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 20px', background: 'var(--deep)', borderTop: '1px solid var(--b0)' }}>
        <button onClick={onCancel} style={{ padding: '10px 16px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', letterSpacing: '.1em' }}>NO, STAY</button>
        <button onClick={onConfirm} style={{ padding: '10px 16px', border: '1px solid var(--red)44', background: 'var(--red)18', color: 'var(--red)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', letterSpacing: '.1em', fontWeight: 700 }}>YES, LOGOUT</button>
      </div>
    </div>
  </div>
);

const TRACKED_USAGE_MODULE_IDS = new Set([
  'auto_eda',
  'doc_analysis',
  'image_insights',
  'audio_video',
  'ml_experiments',
  'dl_experiments',
]);

const PREMIUM_TIER_IDS = new Set(['annual', 'lifetime', 'researcher', 'pro']);

const formatResetDate = (value) => {
  if (!value) return 'the 1st of next month';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'the 1st of next month';
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(dt);
};

const UsageLimitOverlay = ({ mode = 'locked', moduleLabel, nextResetAt, onUpgrade, onOpenUsage }) => {
  const isLoading = mode === 'loading';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        background: 'rgba(2,5,8,.78)',
        backdropFilter: 'blur(5px)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          borderRadius: 8,
          border: `1px solid ${isLoading ? 'var(--b1)' : 'var(--cyan)44'}`,
          background: 'linear-gradient(180deg, rgba(34,211,238,.08), rgba(2,5,8,.95))',
          boxShadow: '0 18px 60px rgba(0,0,0,.35)',
          padding: 18,
        }}
      >
        <div style={{ fontSize: 9, color: isLoading ? 'var(--t3)' : 'var(--cyan)', letterSpacing: '.14em', fontFamily: 'JetBrains Mono', fontWeight: 800 }}>
          {isLoading ? 'SYNCING' : 'FREE TIER EXHAUSTED'}
        </div>
        <div style={{ marginTop: 8, fontFamily: 'Syne', fontWeight: 900, fontSize: 20, color: 'var(--t1)' }}>
          {isLoading ? 'Checking monthly usage' : 'Monthly free limit reached'}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--t2)', lineHeight: 1.8 }}>
          {isLoading
            ? 'Syncing your current allowance before this workspace unlocks.'
            : `${moduleLabel} is used up for this month on the free tier. Next reset starts ${formatResetDate(nextResetAt)}. Upgrade to keep working right now.`}
        </div>
        {!isLoading && (
          <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={onUpgrade}
              style={{
                padding: '10px 14px',
                border: '1px solid var(--cyan)44',
                background: 'var(--gcyan)',
                color: 'var(--cyan)',
                fontSize: 10,
                cursor: 'pointer',
                borderRadius: '4px',
                fontFamily: 'JetBrains Mono',
                letterSpacing: '.1em',
                fontWeight: 800,
              }}
            >
              UPGRADE
            </button>
            <button
              onClick={onOpenUsage}
              style={{
                padding: '10px 14px',
                border: '1px solid var(--b1)',
                background: 'transparent',
                color: 'var(--t2)',
                fontSize: 10,
                cursor: 'pointer',
                borderRadius: '4px',
                fontFamily: 'JetBrains Mono',
                letterSpacing: '.1em',
                fontWeight: 700,
              }}
            >
              VIEW USAGE
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CHECKOUT_PLAN_LABELS = {
  quarterly: 'Researcher Quarterly',
  monthly: 'Researcher Quarterly',
  annual: 'Researcher Yearly',
  lifetime: 'Lifetime Supporter',
};

const CHECKOUT_SYNC_TIMEOUT_MS = 60 * 1000;
const CHECKOUT_SYNC_INTERVAL_MS = 2500;

const getCheckoutReturnFromQuery = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const status = (params.get('checkout') || '').trim().toLowerCase();
  if (status !== 'success') return null;
  return {
    plan: (params.get('plan') || '').trim().toLowerCase(),
  };
};

const getInitialCheckoutRecovery = () => {
  const query = getCheckoutReturnFromQuery();
  if (query) return { ...query, source: 'query', status: 'syncing', retryNonce: 0 };
  return null;
};

const CheckoutSuccessModal = ({ plan, syncStatus = 'ready', onClose, onRetry }) => {
  const planLabel = CHECKOUT_PLAN_LABELS[plan] || 'your new plan';
  const isSyncing = syncStatus === 'syncing';
  const isDelayed = syncStatus === 'delayed';
  const confetti = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    left: `${6 + i * 5}%`,
    delay: `${(i % 6) * 0.12}s`,
    duration: `${2.2 + (i % 5) * 0.18}s`,
    color: ['var(--cyan)', 'var(--gold)', 'var(--green)', 'var(--pink)'][i % 4],
  }));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 260,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        background: 'rgba(2,5,8,.82)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 520,
          overflow: 'hidden',
          borderRadius: 10,
          border: '1px solid var(--cyan)33',
          background: 'linear-gradient(180deg, rgba(34,211,238,.10), rgba(2,5,8,.95))',
          boxShadow: '0 24px 80px rgba(0,0,0,.4)',
          padding: '24px 22px 20px',
        }}
      >
        {confetti.map((piece) => (
          <span
            key={piece.id}
            style={{
              position: 'absolute',
              top: -20,
              left: piece.left,
              width: 8,
              height: 16,
              borderRadius: 2,
              background: piece.color,
              opacity: 0.9,
              transform: 'rotate(18deg)',
              animation: `ddCheckoutConfetti ${piece.duration} linear infinite`,
              animationDelay: piece.delay,
            }}
          />
        ))}

        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 10, color: 'var(--cyan)', letterSpacing: '.16em', fontFamily: 'JetBrains Mono', fontWeight: 900 }}>
            {isSyncing ? 'FINALIZING PAYMENT' : (isDelayed ? 'PAYMENT RECEIVED' : 'PAYMENT SUCCESS')}
          </div>
          <div style={{ marginTop: 10, fontFamily: 'Syne', fontWeight: 900, fontSize: 28, color: 'var(--t1)' }}>
            {isSyncing ? `Activating ${planLabel}` : (isDelayed ? `Still syncing ${planLabel}` : `Welcome to ${planLabel}`)}
          </div>
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--t2)', lineHeight: 1.8 }}>
            {isSyncing
              ? 'Your payment was accepted. We are syncing the paid tier to this account now. This usually takes a few seconds if the webhook or return network call was delayed.'
              : (isDelayed
                ? 'The payment appears complete, but the account update has not finished yet. Try one more sync check. If Lemon was delayed, access will recover automatically on the next app visit too.'
                : 'Your payment went through and premium access is now live on this session.')}
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {isDelayed && (
              <button
                onClick={onRetry}
                style={{
                  padding: '11px 14px',
                  border: 'none',
                  borderRadius: 6,
                  background: 'var(--cyan)',
                  color: '#020508',
                  fontFamily: 'JetBrains Mono',
                  fontSize: 10,
                  letterSpacing: '.12em',
                  fontWeight: 900,
                  cursor: 'pointer',
                }}
              >
                CHECK AGAIN
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '11px 14px',
                border: isDelayed ? '1px solid var(--b1)' : 'none',
                borderRadius: 6,
                background: isDelayed ? 'transparent' : 'var(--cyan)',
                color: isDelayed ? 'var(--t2)' : '#020508',
                fontFamily: 'JetBrains Mono',
                fontSize: 10,
                letterSpacing: '.12em',
                fontWeight: 900,
                cursor: 'pointer',
              }}
            >
              {isSyncing ? 'CLOSE' : (isDelayed ? 'CLOSE' : 'START EXPLORING')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MODULES = [

  { id: 'home', label: 'Home', icon: '⬟', sub: 'Overview · Workspaces · Experiments', color: 'var(--cyan)', cat: 'HOME', component: HomeHub },
  { id: 'auto_eda', label: 'Auto EDA', icon: '📊', sub: 'Cleaning · Visualization · Report', color: 'var(--cyan)', cat: 'HOME', component: AutoEDAHub },
  { id: 'ml_experiments', label: 'ML Experiments', icon: '⚡', sub: 'Feature Engg · Regression · Classification · Report', color: 'var(--orange)', cat: 'HOME', component: MLExperimentsHub },
  { id: 'dl_experiments', label: 'DL Experiments', icon: '🧠', sub: 'Feature Engg · ANN Builder · Train · Test · Metrics · Report', color: 'var(--green)', cat: 'HOME', component: DLExperimentsHub },
  { id: 'doc_analysis', label: 'Document Analysis', icon: '📄', sub: 'Stats · Readbility · Emotion  · Report', color: 'var(--violet)', cat: 'HOME', component: DocumentAnalysisHub },
  { id: 'paper_analysis', label: 'Research Paper Insights', icon: '📑', sub: 'Methodology · Results · Reproduce · Critique', color: 'var(--indigo)', cat: 'HOME', component: PaperAnalysisHub },
  { id: 'image_insights', label: 'Vision Tasks', icon: '🖼️', sub: 'Upload images · Vision + OCR · Export report', color: 'var(--pink)', cat: 'HOME', component: ImageInsightsHub },
  { id: 'audio_video', label: 'Audio & Video [Studio]', icon: '🎥', sub: 'Speech · Transcription · Speakers · Video · Cuts · Scenes · Export', color: 'var(--gold)', cat: 'HOME', component: AudioVideoHub },
  { id: 'local_agents', label: 'AI Agents [Local]', icon: '🤖', sub: 'Local LLM tiers · Model library · Agent roster', color: 'var(--green)', cat: 'HOME', component: LocalAgents },
  { id: 'labs', label: 'Labs [Learning]', icon: '◫', sub: 'Hands-on playground · Browser-based', color: 'var(--violet)', cat: 'HOME', component: LabsHub },
  { id: 'demos', label: 'Tutorials & Demos', icon: '▶', sub: 'Product walkthroughs · Platform reels', color: 'var(--gold)', cat: 'HOME', component: DemosHub },

  { id: 'csv_ds', label: 'CSV Data', icon: '⬟', sub: 'Upload · Clean · Stats · Visualize · Q&A', color: 'var(--cyan)', cat: 'DATA SCIENCE', component: StructuredDS },
  { id: 'excel_ds', label: 'Excel Data', icon: '⬢', sub: 'Multi-sheet · Pivot · GroupBy · Anomaly', color: 'var(--green)', cat: 'DATA SCIENCE', component: ExcelData },
  { id: 'document', label: 'Document Data', icon: '◫', sub: 'NLP · Sentiment · NER · Summary', color: 'var(--violet)', cat: 'DATA SCIENCE', component: DocumentData },
  { id: 'image', label: 'Image Data', icon: '◈', sub: 'Filters · Channels · Pixel Grid', color: 'var(--blue)', cat: 'DATA SCIENCE', component: ImageData },
  { id: 'audio', label: 'Audio Data', icon: '◎', sub: 'Waveform · FFT · MFCC · Bands', color: 'var(--violet)', cat: 'DATA SCIENCE', component: AudioData },
  { id: 'video', label: 'Video Data', icon: '▶', sub: 'Frames · Scenes · Motion · Colors', color: 'var(--orange)', cat: 'DATA SCIENCE', component: VideoData },

  { id: 'ml_feature', label: 'Feature Engineering', icon: '⬟', sub: 'Data profiling · Encoding · Normalization · Transforms · PCA dimensionality reduction', color: 'var(--cyan)', cat: 'MACHINE LEARNING', component: FeatureEng },
  { id: 'ml_regression', label: 'Regression', icon: '◈', sub: 'Simple · Multiple · Polynomial · Ridge/Lasso · Residuals · Interactive playground', color: 'var(--green)', cat: 'MACHINE LEARNING', component: RegressionLab },
  { id: 'ml_classify', label: 'Classification', icon: '◐', sub: 'k-NN · Logistic Regression · Gaussian Naive Bayes · Decision Tree (CART) · ROC/AUC · Model compare', color: 'var(--violet)', cat: 'MACHINE LEARNING', component: ClassificationLab },
  { id: 'ml_cluster', label: 'Clustering', icon: '⬡', sub: 'k-Means++ · Elbow · Silhouette · DBSCAN · Hierarchical Agglomerative · 2D Scatter', color: 'var(--gold)', cat: 'MACHINE LEARNING', component: ClusteringLab },
  { id: 'ml_evaluate', label: 'Model Evaluation', icon: '◉', sub: 'Bias-Variance · Learning Curve · k-Fold CV · Permutation Importance · Metrics Reference', color: 'var(--orange)', cat: 'MACHINE LEARNING', component: EvaluationLab },

  { id: 'dl_neural', label: 'Neural Networks', icon: '⬣', sub: 'Architecture · Forward Pass · Training · Playground · Compare', color: 'var(--cyan)', cat: 'DEEP LEARNING', component: NeuralNetworks },
  { id: 'dl_activations', label: 'Activations', icon: '◎', sub: 'ReLU · Sigmoid · Tanh · Swish · GELU · Saturation · Gradient Flow', color: 'var(--gold)', cat: 'DEEP LEARNING', component: ActivationsLab },
  { id: 'dl_backprop', label: 'Backpropagation', icon: '◉', sub: 'Live Network · Weight Heatmaps · Gradient Flow · Optimizer Race', color: 'var(--violet)', cat: 'DEEP LEARNING', component: BackpropLab },
  { id: 'dl_regularize', label: 'Regularization', icon: '⊕', sub: 'Overfit → Dropout Mask · L1/L2 Weights · BatchNorm Activations · Early Stop', color: 'var(--red)', cat: 'DEEP LEARNING', component: RegularizationLab },
  { id: 'dl_cnn', label: 'CNNs', icon: '▦', sub: 'Kernel Viz · Feature Maps · Pooling · Multi-Filter · Architecture Builder', color: 'var(--blue)', cat: 'DEEP LEARNING', component: CNNLab },
  { id: 'dl_rnn', label: 'RNNs & LSTM', icon: '⟳', sub: 'Unrolled Cells · Gate Activations · Cell State · Vanishing Gradient · Architecture Comparison', color: 'var(--violet)', cat: 'DEEP LEARNING', component: RNNLab },
  { id: 'dl_attention', label: 'Attention', icon: '⬡', sub: 'QKV Flow · Attention Heatmap · Multi-Head · Positional Encoding · Transformer Block', color: 'var(--green)', cat: 'DEEP LEARNING', component: AttentionLab },
  { id: 'dl_training', label: 'Training Dynamics', icon: '◈', sub: 'Loss Curves · LR Schedules · Loss Landscape · Batch Effects · Gradient Health · Full Compare', color: 'var(--orange)', cat: 'DEEP LEARNING', component: TrainingLab },

  { id: 'cv_colorspace', label: 'Color Spaces', icon: '◈', sub: 'RGB · HSV · LAB · YUV · CMYK', color: 'var(--blue)', cat: 'COMPUTER VISION', component: ColorSpaceLab },
  { id: 'cv_edges', label: 'Edge Detection', icon: '◈', sub: 'Sobel · Prewitt · Laplacian · Canny', color: 'var(--violet)', cat: 'COMPUTER VISION', component: EdgeLab },
  { id: 'cv_features', label: 'Feature Extraction', icon: '◈', sub: 'HOG · SIFT · SURF · ORB', color: 'var(--green)', cat: 'COMPUTER VISION', component: FeaturesLab },
  { id: 'cv_filters', label: 'Image Filters', icon: '◈', sub: 'Gaussian · Sharpen · Emboss · Unsharp Masking', color: 'var(--orange)', cat: 'COMPUTER VISION', component: FiltersLab },
  { id: 'cv_frequency', label: 'Frequency Domain', icon: '◈', sub: 'DFT · FFT · Spectral Analysis · Filtering', color: 'var(--cyan)', cat: 'COMPUTER VISION', component: FrequencyLab },
  { id: 'cv_morphology', label: 'Morphological Ops', icon: '◈', sub: 'Dilation · Erosion · Opening · Closing · TopHat', color: 'var(--gold)', cat: 'COMPUTER VISION', component: MorphologyLab },
  { id: 'cv_neural', label: 'Neural CV', icon: '◈', sub: 'CNN Basics · Transfer Learning · Grad-CAM', color: 'var(--green)', cat: 'COMPUTER VISION', component: NeuralCVLab },
  { id: 'cv_segmentation', label: 'Segmentation', icon: '◈', sub: 'Thresholding · Watershed · GrabCut · SLIC Superpixels', color: 'var(--violet)', cat: 'COMPUTER VISION', component: SegmentationLab },

  { id: 'nlp_tokenization', label: 'Tokenization', icon: '◈', sub: 'Words · Sentences · N-grams · Stop Words', color: 'var(--blue)', cat: 'NATURAL LANGUAGE PROCESSING', component: TokenizationLab },
  { id: 'nlp_textstats', label: 'Text Statistics', icon: '◈', sub: 'Word Count · Sentences · Syllables · Readability', color: 'var(--green)', cat: 'NATURAL LANGUAGE PROCESSING', component: TextStatsLab },
  { id: 'nlp_tfidf', label: 'TF-IDF', icon: '◈', sub: 'Term Frequency · Inverse Document Frequency · Vector Space Model', color: 'var(--orange)', cat: 'NATURAL LANGUAGE PROCESSING', component: TFIDFLab },
  { id: 'nlp_sentiment', label: 'Sentiment Analysis', icon: '◈', sub: 'Polarity · Subjectivity · Emotion Detection', color: 'var(--red)', cat: 'NATURAL LANGUAGE PROCESSING', component: SentimentLab },
  { id: 'nlp_ner', label: 'Named Entity Recognition (NER)', icon: '◈', sub: 'Person · Location · Organization · Date/Time Entities', color: 'var(--violet)', cat: 'NATURAL LANGUAGE PROCESSING', component: NERLab },
  { id: 'nlp_langmodel', label: 'Language Models', icon: '◈', sub: 'n-gram · Neural Language Models · Perplexity', color: 'var(--green)', cat: 'NATURAL LANGUAGE PROCESSING', component: LangModelLab },
  { id: 'nlp_embeddings', label: 'Embeddings', icon: '◈', sub: 'Word2Vec · GloVe · FastText · BERT Embeddings', color: 'var(--blue)', cat: 'NATURAL LANGUAGE PROCESSING', component: EmbeddingsLab },
  { id: 'nlp_transformers', label: 'Transformers', icon: '◈', sub: 'Self-Attention · Positional Encoding · BERT · GPT', color: 'var(--violet)', cat: 'NATURAL LANGUAGE PROCESSING', component: TransformersLab },

  { id: 'ge_gan', label: 'GANs', icon: '◈', sub: 'GANs · Diffusion · LLMs · Evaluation · Fine-tuning', color: 'var(--purple)', cat: 'GENERATIVE AI', component: GANLab },
  { id: 'ge_vae', label: 'VAEs', icon: '◈', sub: 'Variational Autoencoders · Latent Space Manipulation', color: 'var(--pink)', cat: 'GENERATIVE AI', component: VAELab },
  { id: 'ge_llm', label: 'LLM Lab', icon: '◈', sub: 'Large Language Models · Prompt Engineering · In-Context Learning', color: 'var(--indigo)', cat: 'GENERATIVE AI', component: LLMLab },
  { id: 'ge_diffusion', label: 'Diffusion', icon: '◈', sub: 'Image Generation · Text-to-Image · Latent Space Manipulation', color: 'var(--teal)', cat: 'GENERATIVE AI', component: DiffusionLab },
  { id: 'ge_eval', label: 'Evaluation', icon: '◈', sub: 'Metrics · Benchmarking · Human Evaluation Tools', color: 'var(--lime)', cat: 'GENERATIVE AI', component: EvalLab },
  { id: 'ge_finetuning', label: 'Fine-tuning', icon: '◈', sub: '. . . ', color: 'var(--olive)', cat: 'GENERATIVE AI', component: FineTuningLab },

  { id: 'ag_core',        label: 'Agent Core',         icon: '🤖', sub: 'Perceive→Think→Act · ReAct · Anatomy · Types',  color: 'var(--cyan)',   cat: 'AGENTIC AI',      component: AgentCoreLab },
  { id: 'ag_memory',      label: 'Memory',             icon: '💾', sub: 'In-context · Vector DB · Episodic · Forgetting', color: 'var(--violet)', cat: 'AGENTIC AI',      component: MemoryLab },
  { id: 'ag_tools',       label: 'Tool Use',           icon: '🔧', sub: 'Function Calling · Schemas · Parallel · Errors', color: 'var(--green)',  cat: 'AGENTIC AI',      component: ToolUseLab },
  { id: 'ag_planning',    label: 'Planning',           icon: '📋', sub: 'CoT · ToT · MCTS · Decompose · Reflect',       color: 'var(--gold)',   cat: 'AGENTIC AI',      component: PlanningLab },
  { id: 'ag_multiagent',  label: 'Multi-Agent',        icon: '🕸', sub: 'Roles · Orchestrator · Debate · Swarm',        color: 'var(--orange)', cat: 'AGENTIC AI',      component: MultiAgentLab },
  { id: 'ag_rag',         label: 'RAG',                icon: '📚', sub: 'Chunk · Embed · Retrieve · Augment · Evaluate', color: 'var(--red)',    cat: 'AGENTIC AI',      component: RAGLab },

  { id: 'rl_qlearn', label: 'Q-Learning', icon: '◈', sub: 'Tabular Q · SARSA · Policy extraction', color: 'var(--green)', cat: 'REINFORCEMENT LEARNING', component: QLearningLab },
  { id: 'rl_policy', label: 'Policy Gradients', icon: '◈', sub: 'REINFORCE · Baselines · Variance reduction', color: 'var(--violet)', cat: 'REINFORCEMENT LEARNING', component: PolicyGradientLab },
  { id: 'rl_deep', label: 'Deep RL', icon: '◈', sub: 'DQN · PPO · DDPG · Replay · Targets', color: 'var(--gold)', cat: 'REINFORCEMENT LEARNING', component: DeepRLLab },
  { id: 'rl_env', label: 'Environment Lab', icon: '◈', sub: 'Gridworlds · Rewards · Observations', color: 'var(--blue)', cat: 'REINFORCEMENT LEARNING', component: EnvLab },
  { id: 'rl_explore', label: 'Exploration', icon: '◈', sub: 'Epsilon · UCB · Intrinsic Curiosity', color: 'var(--orange)', cat: 'REINFORCEMENT LEARNING', component: ExplorationLab },

  { id: 'q_qubit',        label: 'Qubit',              icon: '⚛',  sub: 'Bloch Sphere · Superposition · Measurement · Decoherence · Multi-Qubit States',     color: 'var(--violet)', cat: 'QUANTUM AI',      component: QubitLab },
  { id: 'q_circuits',     label: 'Quantum Circuits',   icon: '🔬', sub: 'Gate Library · Circuit Builder · Interference · Entanglement · Algorithms',  color: 'var(--cyan)',   cat: 'QUANTUM AI',      component: QuantumCircuits },
  { id: 'q_qml',          label: 'QML',                icon: '🧠', sub: 'Variational Circuits · Quantum Kernels · Data Encoding · Quantum Advantage · Landscape',      color: 'var(--green)',  cat: 'QUANTUM AI',      component: QMLLab },
  { id: 'q_optimizer',    label: 'Quantum Optimizer',  icon: '⚡', sub: 'QAOA · Quantum Annealing · TSP · Portfolio Optimization · Classical Comparison',         color: 'var(--gold)',   cat: 'QUANTUM AI',      component: QuantumOptimizer },
  { id: 'q_hybrid',       label: 'Hybrid AI',          icon: '🔗', sub: 'Classical-Quantum Loop · VQE · Noise Mitigation · PennyLane Patterns · Architectures',          color: 'var(--orange)', cat: 'QUANTUM AI',      component: HybridAI },
  { id: 'q_future',       label: 'Quantum Future',     icon: '🚀', sub: 'Timeline · Error Correction · Hardware · Fault Tolerance · Real-World Impact',  color: 'var(--red)',    cat: 'QUANTUM AI',      component: QuantumFuture },
  
{ id: 'edge_brain', label: 'Edge Brain', icon: '🤖', sub: 'Neural Networks · Deep Learning · AI Models', color: '#06b6d4', cat: 'EDGE AI', component: BrainSection },
{ id: 'edge_transformers', label: 'Transformers.js', icon: '🔁', sub: 'BERT · DistilBERT · MobileBERT · TinyBERT', color: '#8b5cf6', cat: 'EDGE AI', component: TransformersSection },
{ id: 'edge_tfjs', label: 'TensorFlow.js', icon: '🧪', sub: 'Layers API · Pre-trained Models · Transfer Learning', color: '#fb923c', cat: 'EDGE AI', component: TensorFlowJSSection },
{ id: 'edge_mediapipe', label: 'MediaPipe', icon: '🖐', sub: 'Face Detection · Hand Tracking · Pose Estimation', color: '#10b981', cat: 'EDGE AI', component: MediaPipeSection },
{ id: 'edge_onnx', label: 'ONNX.js', icon: '📦', sub: 'ONNX Runtime in Browser · Model Zoo · Performance', color: '#ef4444', cat: 'EDGE AI', component: ONNXSection },
{ id: 'edge_faceapi', label: 'face-api.js', icon: '👁️', sub: 'Face Recognition · Expression Analysis', color: '#f472b6', cat: 'EDGE AI', component: FaceAPISection },
]

// Override or normalize icons per-module for consistent UX
const ICON_OVERRIDES = {
  home: '\u2302',
  auto_eda: '\u{1F4CA}',
  doc_analysis: '\u{1F4C4}',
  image_insights: '\u{1F5BC}\uFE0F',
  audio_video: '\u{1F3A5}',
  speech_studio: '\u{1F399}',
  video_intelligence: '\u{1F39E}',
  ml_experiments: '\u26A1',
  dl_experiments: '\u{1F9E0}',
  paper_analysis: '\u{1F4D1}',
  local_agents: '\u{1F916}',

  csv_ds: '\u{1F4CA}',
  excel_ds: '\u{1F4D1}',
  document: '\u{1F4C4}',
  image: '\u{1F5BC}\uFE0F',
  audio: '\u{1F3A7}',
  video: '\u{1F3AC}',

  ml_feature: '\u{1F9E9}',
  ml_regression: '\u{1F4C8}',
  ml_classify: '\u{1F9ED}',
  ml_cluster: '\u{1F310}',
  ml_evaluate: '\u{1F9FE}',

  dl_neural: '\u{1F9E0}',
  dl_activations: '\u2699\uFE0F',
  dl_backprop: '\u{1F501}',
  dl_regularize: '\u{1F6E1}\uFE0F',
  dl_cnn: '\u{1F5BC}\uFE0F',
  dl_rnn: '\u{1F504}',
  dl_attention: '\u{1F3AF}',
  dl_training: '\u{1F3CB}\uFE0F',

  cv_main: '\u{1F4F7}',
  cv_colorspace: '\u{1F3A8}',
  cv_edges: '\u{1F50D}',
  cv_features: '\u{1F50E}',
  cv_filters: '\u{1F9FC}',
  cv_frequency: '\u{1F39A}\uFE0F',
  cv_morphology: '\u{1F527}',
  cv_neural: '\u{1F916}',
  cv_segmentation: '\u2702\uFE0F',

  nlp: '\u{1F5E3}\uFE0F',
  nlp_tokenization: '\u2702\uFE0F',
  nlp_textstats: '\u{1F4DA}',
  nlp_tfidf: '\u{1F524}',
  nlp_sentiment: '\u{1F642}',
  nlp_ner: '\u{1F3F7}\uFE0F',
  nlp_langmodel: '\u{1F916}',
  nlp_embeddings: '\u{1F4CE}',
  nlp_transformers: '\u{1F501}',

  ge_gan: '\u{1F300}',
  ge_vae: '\u{1F9EC}',
  ge_llm: '\u{1F9E0}',
  ge_diffusion: '\u{1F32B}\uFE0F',
  ge_eval: '\u2705',
  ge_finetuning: '\u{1F6E0}\uFE0F',

  ag_core: '\u{1F916}',
  ag_memory: '\u{1F4BE}',
  ag_tools: '\u{1F9F0}',
  ag_planning: '\u{1F5FA}\uFE0F',
  ag_multiagent: '\u{1F578}\uFE0F',
  ag_rag: '\u{1F4DA}',

  rl_qlearn: '\u{1F3AF}',
  rl_policy: '\u{1F4D0}',
  rl_deep: '\u{1F916}',
  rl_env: '\u{1F5FA}\uFE0F',
  rl_explore: '\u{1F9ED}',

  q_qubit: '\u269B\uFE0F',
  q_circuits: '\u{1F52C}',
  q_qml: '\u{1F9EA}',
  q_optimizer: '\u26A1',
  q_hybrid: '\u{1F517}',
  q_future: '\u{1F680}',

  edge_brain: '\u{1F916}',
  edge_transformers: '\u{1F501}',
  edge_tfjs: '\u{1F9EA}',
  edge_mediapipe: '\u{1F590}',
  edge_onnx: '\u{1F4E6}',
  edge_faceapi: '\u{1F441}\uFE0F',
  edge_ml5: '\u2699\uFE0F',
  labs: '\u{1F9ED}',
  demos: '\u{1F3AC}',
};

MODULES.forEach((m) => {
  if (ICON_OVERRIDES[m.id]) m.icon = ICON_OVERRIDES[m.id];
});

for (let index = MODULES.length - 1; index >= 0; index -= 1) {
  const moduleId = MODULES[index].id;
  if (moduleId === 'demos' && !FEATURE_FLAGS.demos) MODULES.splice(index, 1);
  if (moduleId === 'local_agents' && !FEATURE_FLAGS.localAgents) MODULES.splice(index, 1);
}

const SIDEBAR_MODULE_IDS = new Set(['home', 'auto_eda', 'doc_analysis', 'image_insights', 'audio_video', 'ml_experiments', 'dl_experiments', 'paper_analysis', 'local_agents', 'labs', 'demos']);
const SIDEBAR_MODULES = MODULES.filter((module) => SIDEBAR_MODULE_IDS.has(module.id));
const LAB_MODULES = MODULES.filter((module) => !SIDEBAR_MODULE_IDS.has(module.id));
const LAB_CATEGORIES = LAB_MODULES.reduce((groups, module) => {
  const existing = groups.find((group) => group.category === module.cat);
  if (existing) {
    existing.labs.push(module);
  } else {
    groups.push({
      category: module.cat,
      color: module.color,
      labs: [module],
    });
  }
  return groups;
}, []);


const BASE_SITE_URL = SITE_URL;
const DEFAULT_MODULE_ID = 'home';
const MODULE_ID_SET = new Set(MODULES.map((module) => module.id));
const HOME_MOBILE_BREAKPOINT = 980;

const slugifyModuleLabel = (value) =>
  value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');

const MODULE_SEO_LINKS = (() => {
  const used = new Set();
  const map = {};

  MODULES.forEach((module) => {
    const baseSlug = slugifyModuleLabel(module.label) || 'module';
    let slug = baseSlug;
    let attempt = 2;

    if (used.has(slug)) {
      slug = `${baseSlug}-${module.id.replace(/_/g, '-')}`;
    }
    while (used.has(slug)) {
      slug = `${baseSlug}-${attempt}`;
      attempt += 1;
    }

    used.add(slug);
    map[module.id] = {
      slug,
      seoUrl: `${BASE_SITE_URL}/modules/${slug}/`,
    };
  });

  return map;
})();

const alpha = (color, opacity) => `color-mix(in srgb, ${color} ${opacity}%, transparent)`;

const getInitialModuleFromQuery = () => {
  if (typeof window === 'undefined') return DEFAULT_MODULE_ID;
  const moduleId = new URLSearchParams(window.location.search).get('module');
  if (moduleId && MODULE_ID_SET.has(moduleId)) return moduleId;
  return DEFAULT_MODULE_ID;
};

const getActionStoreUserKey = (authUser, isAuthenticated) => {
  if (authUser) return getUserKey(authUser);
  if (!isAuthenticated || typeof window === 'undefined') return 'anon';
  try {
    const token = window.localStorage.getItem('dd_token');
    if (!token) return 'anon';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || payload.id || 'anon';
  } catch (_) {
    return 'anon';
  }
};

export default function App() {
  const [activeModule, setActiveModule] = useState(getInitialModuleFromQuery);
  const [viewProfile, setViewProfile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Subscribe to palette changes so all children re-render with new chart colors
  const { colors: _paletteColors } = useChartPalette();

  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= HOME_MOBILE_BREAKPOINT : false
  );
  const [homeRightPanelOpen, setHomeRightPanelOpen] = useState(
    typeof window !== 'undefined' ? window.innerWidth > HOME_MOBILE_BREAKPOINT : true
  );
  const [rightOpen, setRightOpen] = useState(true);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [instructor, setInstructor] = useState(false);
  const [time, setTime] = useState(new Date());
  const [labsFocusCategory, setLabsFocusCategory] = useState(null);
  const currentPath = typeof window !== 'undefined'
    ? (
        normalizePublicReportPath(new URLSearchParams(window.location.search).get('public_report'))
        || window.location.pathname.replace(/\/+$/, '')
        || '/'
      )
    : '/';
  const isPricingPage = currentPath === '/pricing';

  const { user: authUser, logout, isAuthenticated, loading, refreshUser } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authView, setAuthView] = useState('login');
  const [usageOpen, setUsageOpen] = useState(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [membershipFocus, setMembershipFocus] = useState(null);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [usageSummary, setUsageSummary] = useState(null);

  const [usageSummaryLoading, setUsageSummaryLoading] = useState(false);
  const [usageReloadKey, setUsageReloadKey] = useState(0);
  const [checkoutRecovery, setCheckoutRecovery] = useState(getInitialCheckoutRecovery);
  const [workspaceRestore, setWorkspaceRestore] = useState(null);
  const hubMountKeyRef = useRef(0);
  const [hubMountKey, setHubMountKey] = useState(0);
  const navigateModule = useCallback((id) => {
    hubMountKeyRef.current += 1;
    setHubMountKey(hubMountKeyRef.current);
    setActiveModule(id);
    setViewProfile(false);
  }, []);
  const prevModuleRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated && !authUser) return undefined;
    const userKey = getActionStoreUserKey(authUser, isAuthenticated);
    return initActionStore(userKey);
  }, [authUser, isAuthenticated]);

  useEffect(() => {
    if (!activeModule) return;
    if (typeof window !== 'undefined') {
      window.__dd_active_module = activeModule;
    }
    const prev = prevModuleRef.current;
    if (prev === null) {
      prevModuleRef.current = activeModule;
      return;
    }
    if (prev !== activeModule) {
      clearSession(getActionStoreUserKey(authUser, isAuthenticated));
      trackAction({
        module: activeModule,
        type: 'ui:navigate',
        label: `Navigate to ${String(activeModule).toUpperCase()}`,
        group: 'explore',
        icon: '🧭',
        params: { from: prev, to: activeModule },
        replayable: false,
      });
      prevModuleRef.current = activeModule;
    }
  }, [activeModule, authUser, isAuthenticated]);

  useEffect(() => {
    const h = (e) => {
      const view = e?.detail === 'signup' ? 'signup' : 'login';
      setAuthView(view);
      setAuthOpen(true);
    };
    window.addEventListener('open-auth', h);
    return () => window.removeEventListener('open-auth', h);
  }, []);

  useEffect(() => {
    const h = (e) => {
      const detail = e?.detail;
      const focus =
        typeof detail === 'string'
          ? detail
          : (detail && typeof detail === 'object' ? (detail.requires || detail.focus || null) : null);
      setMembershipFocus(focus);
      setMembershipOpen(true);
    };
    window.addEventListener('open-membership', h);
    return () => window.removeEventListener('open-membership', h);
  }, []);

  useEffect(() => {
    const h = () => setUsageOpen(true);
    window.addEventListener('open-usage', h);
    return () => window.removeEventListener('open-usage', h);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const workspace = event?.detail;
      if (!workspace?.module_id) return;
      if (!isAuthenticated) {
        setAuthView('signup');
        setAuthOpen(true);
        return;
      }
      setWorkspaceRestore(workspace);
      setActiveModule(workspace.module_id);
      setViewProfile(false);
    };
    window.addEventListener('dd-workspace-restore', handler);
    return () => window.removeEventListener('dd-workspace-restore', handler);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!checkoutRecovery || checkoutRecovery.source !== 'query' || typeof window === 'undefined') return undefined;

    const url = new URL(window.location.href);
    url.searchParams.delete('checkout');
    url.searchParams.delete('plan');
    const nextSearch = url.searchParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`
    );
    return undefined;
  }, [checkoutRecovery]);

  useEffect(() => {
    if (!checkoutRecovery || checkoutRecovery.status !== 'syncing' || loading || !isAuthenticated) return undefined;

    let cancelled = false;
    let timerId;
    const deadline = Date.now() + CHECKOUT_SYNC_TIMEOUT_MS;

    const syncMembership = async () => {
      try {
        const status = await api.payments.status({ reconcile: 1 });
        if (cancelled) return;
        if (status?.is_premium) {
          clearPendingCheckout();
          try {
            await refreshUser?.();
          } catch (_) {}
          if (cancelled) return;
          setUsageReloadKey((value) => value + 1);
          setCheckoutRecovery((current) => (current ? { ...current, status: 'ready' } : current));
          return;
        }
      } catch (_) {}

      if (cancelled) return;
      if (Date.now() >= deadline) {
        setCheckoutRecovery((current) => (current ? { ...current, status: 'delayed' } : current));
        return;
      }
      timerId = window.setTimeout(syncMembership, CHECKOUT_SYNC_INTERVAL_MS);
    };

    syncMembership();
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [checkoutRecovery, isAuthenticated, loading, refreshUser]);

  useEffect(() => {
    if (checkoutRecovery || loading || !isAuthenticated || authUser?.tier !== 'free') return undefined;

    let cancelled = false;
    const pendingCheckout = getPendingCheckout();

    api.payments.status(pendingCheckout ? { reconcile: 1 } : {})
      .then(async (status) => {
        if (cancelled) return;
        if (status?.is_premium) {
          clearPendingCheckout();
          try {
            await refreshUser?.();
          } catch (_) {}
          if (!cancelled) setUsageReloadKey((value) => value + 1);
          return;
        }
        if (!status?.has_pending_checkout) clearPendingCheckout();
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [authUser?.tier, checkoutRecovery, isAuthenticated, loading, refreshUser]);

  useEffect(() => {
    if (!isAuthenticated && viewProfile) setViewProfile(false);
  }, [isAuthenticated, viewProfile]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUsageSummary(null);
      setUsageSummaryLoading(false);
      return undefined;
    }

    let cancelled = false;
    setUsageSummaryLoading(true);

    api.usage.summary()
      .then((data) => {
        if (!cancelled) setUsageSummary(data);
      })
      .catch(() => {
        if (!cancelled) setUsageSummary(null);
      })
      .finally(() => {
        if (!cancelled) setUsageSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, usageReloadKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const refreshUsage = () => setUsageReloadKey((value) => value + 1);

    window.addEventListener('dd-usage-changed', refreshUsage);
    window.addEventListener('dd-usage-limit-reached', refreshUsage);
    return () => {
      window.removeEventListener('dd-usage-changed', refreshUsage);
      window.removeEventListener('dd-usage-limit-reached', refreshUsage);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const collapseLeft = () => setSidebarOpen(false);
    window.addEventListener('dd-collapse-left', collapseLeft);
    return () => window.removeEventListener('dd-collapse-left', collapseLeft);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined' || isAuthenticated) return undefined;

    const openSignupGate = () => {
      setAuthView('signup');
      setAuthOpen(true);
    };

    const stopEvent = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    };

    const onFileInputClickCapture = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if ((target.type || '').toLowerCase() !== 'file') return;
      stopEvent(event);
      openSignupGate();
    };

    const onFileInputChangeCapture = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if ((target.type || '').toLowerCase() !== 'file') return;
      stopEvent(event);
      target.value = '';
      openSignupGate();
    };

    const onFileDropCapture = (event) => {
      const dataTransfer = event.dataTransfer;
      if (!dataTransfer || !dataTransfer.files || dataTransfer.files.length === 0) return;
      stopEvent(event);
      openSignupGate();
    };

    document.addEventListener('click', onFileInputClickCapture, true);
    document.addEventListener('change', onFileInputChangeCapture, true);
    document.addEventListener('drop', onFileDropCapture, true);

    return () => {
      document.removeEventListener('click', onFileInputClickCapture, true);
      document.removeEventListener('change', onFileInputChangeCapture, true);
      document.removeEventListener('drop', onFileDropCapture, true);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncViewport = () => {
      const mobile = window.innerWidth <= HOME_MOBILE_BREAKPOINT;
      setIsMobileViewport(mobile);
      setHomeRightPanelOpen(!mobile);
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    if (isMobileViewport && sidebarOpen) setSidebarOpen(false);
  }, [isMobileViewport, sidebarOpen]);

  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (isPricingPage) return;

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.searchParams.get('module') !== activeModule) {
        url.searchParams.set('module', activeModule);
        const nextPath = `${url.pathname}?${url.searchParams.toString()}${url.hash}`;
        window.history.replaceState(window.history.state, '', nextPath);
      }
    }

    if (typeof document === 'undefined') return;
    const currentModule = MODULES.find((module) => module.id === activeModule);
    if (!currentModule) return;

    const moduleSummary = (currentModule.sub || '')
      .replace(/\s*·\s*/g, ', ')
      .replace(/\s+/g, ' ')
      .trim();
    const description = `${currentModule.label} lab in the ${currentModule.cat} track. Explore ${moduleSummary}.`;
    const keywords = [
      currentModule.label,
      `${currentModule.label} lab`,
      currentModule.cat,
      'AI lab',
      'Jorvex',
      'interactive learning',
    ]
      .map((value) => value.toLowerCase())
      .join(', ');

    const upsertMeta = (attribute, key, content) => {
      let node = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!node) {
        node = document.createElement('meta');
        node.setAttribute(attribute, key);
        document.head.appendChild(node);
      }
      node.setAttribute('content', content);
    };

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    const canonicalUrl = MODULE_SEO_LINKS[activeModule]?.seoUrl || `${BASE_SITE_URL}/`;

  document.title = `${currentModule.label} | Jorvex`;
    canonical.setAttribute('href', canonicalUrl);
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('property', 'og:title', document.title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('name', 'twitter:title', document.title);
    upsertMeta('name', 'twitter:description', description);
  }, [activeModule, isPricingPage]);

  const mod = MODULES.find(m => m.id === activeModule);
  const sidebarQuery = sidebarSearch.trim().toLowerCase();
  const filteredModules = sidebarQuery
    ? SIDEBAR_MODULES.filter((m) =>
      [m.label, m.sub, m.cat, m.id].some((value) =>
        String(value || '').toLowerCase().includes(sidebarQuery)
      )
    )
    : SIDEBAR_MODULES;
  const visibleCats = [...new Set(filteredModules.map((m) => m.cat))];
  const isHomeActive = activeModule === 'home';
  const isAutoEDAHubActive = activeModule === 'auto_eda';
  const isDocumentAnalysisActive = activeModule === 'doc_analysis';
  const isMLExperimentsHubActive = activeModule === 'ml_experiments';
  const isDLExperimentsHubActive = activeModule === 'dl_experiments';
  const isLocalAgentsActive = activeModule === 'local_agents';
  const isLabsHubActive = activeModule === 'labs';
  const isDemosHubActive = activeModule === 'demos';
  const isLabModuleActive = !!mod && !SIDEBAR_MODULE_IDS.has(mod.id);
  const homeMissionTitle = "HUMANS + AI\s DO AI RESEARCH TOGETHER HERE";
  const moduleMode = 'ENGINEER';
  const authDisplayName =
    authUser?.full_name ||
    authUser?.username ||
    authUser?.name ||
    authUser?.fullName ||
    (authUser?.email ? authUser.email.split('@')[0] : '') ||
    'User';
  const authMeta =
    authUser?.tier === 'researcher' || authUser?.tier === 'annual' || authUser?.tier === 'pro' || authUser?.tier === 'lifetime'
      ? 'Paid Tier'
      : 'Free Tier';
  const isPremiumAccount = PREMIUM_TIER_IDS.has(String(authUser?.tier || '').toLowerCase()) || !!usageSummary?.is_premium;
  const activeModuleUsage = usageSummary?.modules?.[activeModule] || null;
  const activeTrackedModuleLocked = Boolean(
    isAuthenticated &&
    !isPremiumAccount &&
    TRACKED_USAGE_MODULE_IDS.has(activeModule) &&
    activeModuleUsage?.blocked
  );
  const activeTrackedModuleLoading = Boolean(
    isAuthenticated &&
    !isPremiumAccount &&
    TRACKED_USAGE_MODULE_IDS.has(activeModule) &&
    usageSummaryLoading &&
    !usageSummary
  );
  const confirmLogout = () => {
    setLogoutConfirmOpen(true);
  };
  const handleLogoutConfirmed = () => {
    setLogoutConfirmOpen(false);
    logout();
    setViewProfile(false);
  };
  const openAuth = (view = 'login') => {
    setAuthView(view === 'signup' ? 'signup' : 'login');
    setAuthOpen(true);
  };
  const openPricingPage = () => {
    setMembershipFocus(null);
    setMembershipOpen(true);
  };
  const openUpgradeFromLimit = () => {
    setMembershipFocus('researcher');
    setMembershipOpen(true);
  };
  const openLabsHub = (category = null) => {
    setLabsFocusCategory(category);
    setActiveModule('labs');
    setViewProfile(false);
  };
  const openLabModule = (module) => {
    setLabsFocusCategory(module?.cat || null);
    setActiveModule(module.id);
    setViewProfile(false);
  };
  const handleWorkspaceRestore = (workspace) => {
    if (!workspace?.module_id) return;
    if (!isAuthenticated) {
      openAuth('signup');
      return;
    }
    setWorkspaceRestore(workspace);
    setActiveModule(workspace.module_id);
    setViewProfile(false);
  };
  const toggleHelpPanel = () => {
    setHelpOpen((prev) => {
      const next = !prev;
      if (next) setNotifOpen(false);
      return next;
    });
  };
  const toggleNotifPanel = () => {
    setNotifOpen((prev) => {
      const next = !prev;
      if (next) setHelpOpen(false);
      return next;
    });
  };

  if (isPricingPage) {
    return (
      <>
        <PricingPage onAuthOpen={openAuth} />
        {authOpen && <AuthModal onClose={() => setAuthOpen(false)} defaultView={authView} />}
        <ChatSystem />
      </>
    );
  }

  // ── /reset-password page ──────────────────────────────────────────────────
  if (currentPath === '/reset-password') {
    return <ResetPasswordPage />;
  }

  // ── /about page — marketing landing page ─────────────────────────────────
  if (currentPath === '/about') {
    return (
      <>
        <MarketingLandingPage
          onSignup={() => { openAuth('signup'); }}
          onLogin={() => { openAuth('login'); }}
          onEnterApp={() => { openAuth('signup'); }}
          onViewPricing={() => { window.location.href = '/pricing'; }}
        />
        {authOpen && <AuthModal onClose={() => setAuthOpen(false)} defaultView={authView} />}
        <ChatSystem />
      </>
    );
  }

  if (currentPath.startsWith('/reports/')) {
    return (
      <>
        <PublicReportPage currentPath={currentPath} onAuthOpen={openAuth} />
        {authOpen && <AuthModal onClose={() => setAuthOpen(false)} defaultView={authView} />}
        <ChatSystem />
      </>
    );
  }

  const renderModule = () => {
    if (viewProfile) return <ProfilePage user={{ name: authDisplayName }} onClose={() => setViewProfile(false)} />;
    const M = mod?.component;
    if (M === HomeHub || M === AutoEDAHub || M === DocumentAnalysisHub || M === ImageInsightsHub || M === AudioVideoHub || M === MLExperimentsHub || M === DLExperimentsHub || M === PaperAnalysisHub || M === LocalAgents || M === LabsHub || M === DemosHub) {
      const hubCenter =
        M === HomeHub ? (
          <HomeHub
            mode={moduleMode}
            isAuthenticated={isAuthenticated}
            authLoading={loading}
            authDisplayName={authDisplayName}
            authUser={authUser}
            isPremium={isPremiumAccount}
            usageSummary={usageSummary}
            onNavigate={navigateModule}
            onAuthOpen={openAuth}
            onUpgrade={openUpgradeFromLimit}
          />
        ) : M === AutoEDAHub ? (
          <AutoEDAHub key={hubMountKey} onBack={() => navigateModule('home')} isAuthenticated={isAuthenticated} authUser={authUser} workspaceRestore={workspaceRestore} onWorkspaceRestored={() => setWorkspaceRestore(null)} />
        ) : M === DocumentAnalysisHub ? (
          <DocumentAnalysisHub key={hubMountKey} onBack={() => navigateModule('home')} isAuthenticated={isAuthenticated} authUser={authUser} workspaceRestore={workspaceRestore} onWorkspaceRestored={() => setWorkspaceRestore(null)} />
        ) : M === ImageInsightsHub ? (
          <ImageInsightsHub key={hubMountKey} onBack={() => navigateModule('home')} isAuthenticated={isAuthenticated} workspaceRestore={workspaceRestore} onWorkspaceRestored={() => setWorkspaceRestore(null)} />
        ) : M === AudioVideoHub ? (
          <AudioVideoHub key={hubMountKey} onBack={() => navigateModule('home')} isAuthenticated={isAuthenticated} workspaceRestore={workspaceRestore} onWorkspaceRestored={() => setWorkspaceRestore(null)} />
        ) : M === MLExperimentsHub ? (
          <MLExperimentsHub key={hubMountKey} onBack={() => navigateModule('home')} isAuthenticated={isAuthenticated} authUser={authUser} workspaceRestore={workspaceRestore} onWorkspaceRestored={() => setWorkspaceRestore(null)} />
        ) : M === DLExperimentsHub ? (
          <DLExperimentsHub key={hubMountKey} onBack={() => navigateModule('home')} isAuthenticated={isAuthenticated} authUser={authUser} workspaceRestore={workspaceRestore} onWorkspaceRestored={() => setWorkspaceRestore(null)} />
        ) : M === PaperAnalysisHub ? (
          <PaperAnalysisHub key={hubMountKey} onBack={() => navigateModule('home')} isAuthenticated={isAuthenticated} authUser={authUser} workspaceRestore={workspaceRestore} onWorkspaceRestored={() => setWorkspaceRestore(null)} />
        ) : M === LocalAgents ? (
          <LocalAgents />
        ) : M === LabsHub ? (
          <LabsHub
            labCategories={LAB_CATEGORIES}
            focusedCategory={labsFocusCategory}
            onOpenLab={openLabModule}
          />
        ) : (
          <DemosHub />
        );
      const gatedHubCenter = (
        <div style={{ position: 'relative', minHeight: '100%' }}>
          <div style={(activeTrackedModuleLocked || activeTrackedModuleLoading)
            ? { pointerEvents: 'none', userSelect: 'none', filter: 'blur(1px)', opacity: 0.3 }
            : undefined}>
            {hubCenter}
          </div>
          {(activeTrackedModuleLocked || activeTrackedModuleLoading) && (
            <UsageLimitOverlay
              mode={activeTrackedModuleLoading ? 'loading' : 'locked'}
              moduleLabel={mod?.label || 'This workspace'}
              nextResetAt={usageSummary?.next_reset_at}
              onUpgrade={openUpgradeFromLimit}
              onOpenUsage={() => setUsageOpen(true)}
            />
          )}
        </div>
      );
      const hubRightInner = (
        <div style={{ minHeight: 0, overflowY: 'auto', border: '1px solid var(--b1)', borderRadius: '4px', background: 'var(--deep)', padding: '10px', height: '100%' }}>
          <RightPanel
            activeModule={activeModule}
            isAuthenticated={isAuthenticated}
            authUser={authUser}
            onNavigate={navigateModule}
          />
        </div>
      );

      

      if (isMobileViewport) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, minHeight: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button
                onClick={() => setHomeRightPanelOpen((prev) => !prev)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid var(--b1)',
                  background: homeRightPanelOpen ? 'var(--gcyan)' : 'transparent',
                  color: homeRightPanelOpen ? 'var(--cyan)' : 'var(--t2)',
                  fontSize: '9px',
                  cursor: 'pointer',
                  borderRadius: '2px',
                  fontFamily: 'JetBrains Mono',
                  letterSpacing: '.08em',
                  fontWeight: 600,
                }}
              >
                {homeRightPanelOpen ? 'HIDE SIDE PANEL' : 'SHOW SIDE PANEL'}
              </button>
            </div>
            <div style={{ minHeight: 0, overflowY: (M === HomeHub || M === AutoEDAHub || M === DocumentAnalysisHub || M === ImageInsightsHub || M === AudioVideoHub || M === MLExperimentsHub || M === DLExperimentsHub || M === PaperAnalysisHub || M === LocalAgents) ? 'auto' : 'hidden', overflowX: 'hidden', flex: 1 }}>{gatedHubCenter}</div>
            {homeRightPanelOpen && (
              <div style={{ maxHeight: '42vh', minHeight: 0 }}>
                {hubRightInner}
              </div>
            )}
          </div>
        );
      }

      const overflowY = (M === HomeHub || M === AutoEDAHub || M === DocumentAnalysisHub || M === ImageInsightsHub || M === AudioVideoHub || M === MLExperimentsHub || M === DLExperimentsHub || M === PaperAnalysisHub || M === LocalAgents) ? 'auto' : 'hidden';
      return (
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* ── Center (hub content) ── */}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY, overflowX: 'hidden' }}>
            {gatedHubCenter}
          </div>

          {/* ── Main ↔ Right-panel collapse handle ── */}
          <div style={{ width: 0, position: 'relative', flexShrink: 0, zIndex: 50 }}>
            <button
              onClick={() => setRightOpen(p => !p)}
              title={rightOpen ? 'Collapse right panel' : 'Expand right panel'}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 22px rgba(34,211,238,.45), 0 2px 12px rgba(0,0,0,.7)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,.75)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 12px rgba(34,211,238,.18), 0 2px 10px rgba(0,0,0,.6)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,.38)'; }}
              style={{
                position: 'absolute',
                left: -11,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 22, height: 46,
                background: 'rgba(6,11,20,.96)',
                border: '1px solid rgba(34,211,238,.38)',
                borderRadius: 11,
                color: '#22d3ee',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                fontSize: 13, fontWeight: 800, fontFamily: 'JetBrains Mono',
                boxShadow: '0 0 12px rgba(34,211,238,.18), 0 2px 10px rgba(0,0,0,.6)',
                transition: 'box-shadow .15s, border-color .15s',
                padding: 0, lineHeight: 1, userSelect: 'none',
              }}
            >
              {rightOpen ? '›' : '‹'}
            </button>
          </div>

          {/* ── Right panel (collapsible) ── */}
          <div style={{
            flexShrink: 0,
            width: rightOpen ? 312 : 0,
            overflow: 'hidden',
            transition: 'width .28s ease',
            minHeight: 0,
          }}>
            <div style={{ width: 312, paddingLeft: 12, height: '100%', boxSizing: 'border-box' }}>
              {hubRightInner}
            </div>
          </div>
        </div>
      );
    }
    if (M) return <M mode={moduleMode} />;
    return <SimPlaceholder label={mod?.label || ''} />;
  };

  const HELP_GUIDES = {
    csv_ds: [
      ['①', 'UPLOAD', 'Drop .csv file or load a sample dataset.'],
      ['②', 'OVERVIEW', 'Column manifest with types, null rates, data preview.'],
      ['③', 'CLEAN', 'Drop nulls, fill mean/median/mode, dedup, normalize, z-score.'],
      ['④', 'DISTRIBUTION', 'Histogram, box plot, value counts per column.'],
      ['⑤', 'CORRELATION', 'Pearson heatmap + scatter explorer.'],
      ['⑥', 'VISUALIZE', 'Bar, line, area, pie, scatter, histogram chart builder.'],
      ['⑦', 'Q&A', 'Natural language questions about your data.'],
    ],
    excel_ds: [
      ['①', 'UPLOAD', 'Drop .xlsx .xls .ods — parsed 100% in-browser via SheetJS.'],
      ['②', 'WORKBOOK', 'All sheets overview, formula detection, merged cells, data preview.'],
      ['③', 'PROFILE', 'Per-column quality score, stats, top values, null rates, issues.'],
      ['④', 'STATS', 'Full stats suite: mean/std/skew/kurtosis, percentiles, box plot.'],
      ['⑤', 'PIVOT', 'Drag-and-drop pivot table builder with 7 aggregation methods.'],
      ['⑥', 'GROUP BY', 'Group by any column, aggregate with sum/mean/count/std/min/max.'],
      ['⑦', 'TIME SERIES', 'Auto-detect date columns, moving average, trend, growth rates.'],
      ['⑧', 'ANOMALIES', 'Z-score + IQR outlier detection across all numeric columns.'],
      ['⑨', 'CORRELATE', 'Full Pearson correlation matrix heatmap.'],
      ['⑩', 'VISUALIZE', 'Bar/Line/Area/Scatter/Pie/Histogram/Radar chart builder.'],
      ['⑪', 'MISSING', 'Cell-level missing value heatmap + null rate per column.'],
    ],
    document: [
      ['①', 'INPUT', 'Drop .txt/.md/.json/code or paste text. Analysis runs automatically.'],
      ['②', 'STATS', 'Word count, sentence length, lexical diversity, POS distribution, TTR curve.'],
      ['③', 'FREQUENCY', 'Word freq + Zipf\'s law, bigrams, trigrams, character frequency.'],
      ['④', 'READABILITY', '6 scores: Flesch, FK Grade, Fog, SMOG, Coleman-Liau, ARI.'],
      ['⑤', 'SENTIMENT', 'Overall + per-sentence sentiment arc. Negation + intensifier handling.'],
      ['⑥', 'ENTITIES', 'NER: persons, orgs, locations, dates, money, %, emails, URLs.'],
      ['⑦', 'KEYWORDS', 'TF-IDF ranked keywords + visual word cloud.'],
      ['⑧', 'TOPICS', 'Co-occurrence topic clusters + repetition/redundancy analysis.'],
      ['⑨', 'SUMMARY', 'Extractive summarization with sentence importance scores.'],
      ['⑩', 'COMPARE', 'Cosine + Jaccard similarity between two documents.'],
    ],
    video: [
      ['①', 'UPLOAD', 'Drop any video file. All analysis runs in-browser — no server upload.'],
      ['②', 'OVERVIEW', 'Metadata, frame strip, dominant color palette.'],
      ['③', 'TIMELINE', 'Brightness, contrast, motion intensity, RGB evolution over time.'],
      ['④', 'SCENES', 'Auto scene detection (frame diff threshold). Thumbnail grid + timeline bar.'],
      ['⑤', 'COLORS', 'Per-frame palettes, hue distribution, global k-means palette.'],
      ['⑥', 'MOTION', 'Block-based optical flow overlay, motion vs brightness scatter.'],
      ['⑦', 'FRAMES', 'Browse individual frames, per-frame stats + palette.'],
      ['⑧', 'AUDIO', 'RMS energy over time, peak/RMS dBFS, audio+motion correlation.'],
    ],
    image: [
      ['①', 'UPLOAD', 'Drop image or use synthetic test pattern.'],
      ['②', 'FILTERS', '17 Canvas API filters with live sliders.'],
      ['③', 'CHANNELS', 'R/G/B isolation, stats table, channel radar.'],
      ['④', 'HISTOGRAM', 'Intensity distributions per channel.'],
      ['⑤', 'PIXEL GRID', 'Hover to inspect R/G/B. Luminance heatmap.'],
      ['⑥', 'COMPARE', 'All 12 filters side-by-side.'],
    ],
    audio: [
      ['①', 'UPLOAD', 'Upload audio or generate synthetic test signal.'],
      ['②', 'WAVEFORM', 'Amplitude over time. Peak vs trough. Play button.'],
      ['③', 'SPECTRUM', 'Real Cooley-Tukey FFT. Magnitude or dB.'],
      ['④', 'SPECTROGRAM', 'Short-time FFT heatmap. Hanning windowed.'],
      ['⑤', 'FREQ BANDS', 'Sub-bass → brilliance energy. Radar chart.'],
      ['⑥', 'MFCC', '13 DCT coefficients with interpretation.'],
    ],
    structured_ds: [
      ['①', 'UPLOAD', 'Drop CSV or use a sample dataset.'],
      ['②', 'OVERVIEW', 'Column manifest, null rates, data preview.'],
      ['③', 'CLEAN', 'All cleaning ops ACTUALLY modify data.'],
      ['④', 'DISTRIBUTION', 'Histogram, box plot, value counts.'],
      ['⑤', 'CORRELATION', 'Heatmap + scatter explorer.'],
      ['⑥', 'VISUALIZE', 'Bar, line, area, pie, scatter, histogram.'],
      ['⑦', 'Q&A', 'Natural language questions about your data.'],
    ],
    excel_ds: [
      ['①', 'UPLOAD', 'Drop .xlsx/.xls/.ods or generate sample workbooks.'],
      ['②', 'WORKBOOK', 'Multi-sheet overview — formulas, merges, cross-sheet summary.'],
      ['③', 'PROFILE', 'Per-column quality scoring — nulls, outliers, skewness, top values.'],
      ['④', 'STATS', 'Full statistics: percentiles, skewness, kurtosis, IQR, box plot.'],
      ['⑤', 'PIVOT', 'Interactive pivot table builder — 7 aggregation functions.'],
      ['⑥', 'GROUP BY', 'Group-by aggregation with horizontal bar chart + full table.'],
      ['⑦', 'TIME SERIES', 'Date auto-detect, MA, linear trend, growth rates.'],
      ['⑧', 'ANOMALIES', 'Z-score + IQR dual detection, per-column outlier fences.'],
      ['⑨', 'CORRELATE', 'NxN Pearson correlation heatmap + ranked pair list.'],
      ['⑩', 'VISUALIZE', 'Bar, line, area, scatter, pie, histogram, radar charts.'],
      ['⑪', 'MISSING', 'Cell-level null heatmap + per-column null rate chart.'],
    ],
    ml_feature: [
      ['①', 'UPLOAD', 'Drop CSV or use sample dataset.'],
      ['②', 'OVERVIEW', 'Column profiles: mean, std, skew, kurtosis, outliers, nulls for every column.'],
      ['③', 'DISTRIBUTIONS', 'Histogram + box plot per column. Q1/Q2/Q3 visualized.'],
      ['④', 'CORRELATIONS', 'Mutual information scores + Pearson correlation heatmap.'],
      ['⑤', 'ENCODE', 'One-hot, label encoding, binning (equal-width/freq), min-max/z-score normalization.'],
      ['⑥', 'TRANSFORM', 'Log transform, interaction features (C1×C2), polynomial degree-2 features.'],
      ['⑦', 'PCA', 'Principal Component Analysis — scree plot, scatter, feature loadings.'],
      ['⑧', 'EXPORT', 'Download engineered CSV with all new features highlighted.'],
    ],
    ml_regression: [
      ['①', 'UPLOAD', 'Drop CSV — needs numeric features + numeric target.'],
      ['②', 'SIMPLE', 'OLS simple linear regression — equation, R², residuals, residual distribution.'],
      ['③', 'MULTIPLE', 'Gradient descent MLR — feature coefficients, loss curve, predicted vs actual.'],
      ['④', 'POLYNOMIAL', 'Degree 1/2/3 comparison — R² and RMSE across all three.'],
      ['⑤', 'RIDGE/LASSO', 'Regularization sweep λ=0..10 — R² and RMSE vs lambda charts.'],
      ['⑥', 'DIAGNOSTICS', 'Learning curve — train vs test R² as dataset size grows.'],
      ['⑦', 'PLAYGROUND', 'Live prediction — enter feature values, see instant model output.'],
    ],
    ml_classify: [
      ['①', 'UPLOAD', 'Drop CSV — numeric features + categorical target.'],
      ['②', 'SETUP', 'Class distribution bar chart, feature/label selection, test split control.'],
      ['③', 'k-NN', 'k-Nearest Neighbors — adjustable k, confusion matrix, per-class precision/recall/F1.'],
      ['④', 'LOGISTIC', 'Logistic regression — training loss, ROC curve, AUC score.'],
      ['⑤', 'NAIVE BAYES', 'Gaussian NB — class priors, per-feature Gaussian parameters (μ, σ).'],
      ['⑥', 'DTREE', 'Decision Tree CART — Gini splits, tree visualizer, depth-5 rules.'],
      ['⑦', 'COMPARE', 'Side-by-side accuracy + macro F1 bar charts + ranked leaderboard.'],
    ],
    ml_cluster: [
      ['①', 'UPLOAD', 'Drop CSV — numeric features. No labels needed.'],
      ['②', 'SETUP', 'Select features for clustering + 2D scatter axes.'],
      ['③', 'k-MEANS', 'k-Means++ — adjustable k, silhouette score, convergence history, cluster stats.'],
      ['④', 'ELBOW', 'Inertia vs k + Silhouette vs k — dual charts to find optimal k.'],
      ['⑤', 'DBSCAN', 'ε + minPts sliders — finds arbitrary-shaped clusters, marks noise points.'],
      ['⑥', 'HIERARCHICAL', 'Agglomerative clustering — dendrogram, cluster scatter.'],
      ['⑦', 'INSIGHTS', 'Algorithm comparison: k-Means vs DBSCAN vs Hierarchical cheat sheet.'],
    ],
    ml_evaluate: [
      ['①', 'UPLOAD', 'Drop CSV — numeric features + numeric target.'],
      ['②', 'SETUP', 'Select features and target column.'],
      ['③', 'BIAS-VARIANCE', '10 bootstrap trials × 5 complexity levels — bias², variance, sweet spot table.'],
      ['④', 'LEARNING CURVE', 'Train vs test R² as data grows — detects underfitting/overfitting.'],
      ['⑤', 'CROSS-VAL', '5-Fold CV — per-fold train/test R², average ± std. Fold comparison bar chart.'],
      ['⑥', 'PERMUTATION', 'Model-agnostic feature importance — R² drop when each feature is shuffled.'],
      ['⑦', 'METRICS', 'Full reference: regression, classification, clustering, CV concepts with ranges and guidance.'],
    ],
  };

  const guide = HELP_GUIDES[activeModule] || [];
  const notifications = [
    {
      id: 'n1',
      actor: '@neural_x',
      actorType: 'HUMAN',
      type: 'COMMENT',
      text: 'Reviewed your CNN compression run. Try lower-rank projection before pruning.',
      context: 'on CIFAR-10 challenge thread',
      time: '2m',
    },
    {
      id: 'n2',
      actor: 'Astra-7B',
      actorType: 'AI',
      type: 'AI NOTE',
      text: 'Generated 3 candidate hyperparameter sweeps and attached expected accuracy deltas.',
      context: 'in your agent workspace',
      time: '6m',
    },
    {
      id: 'n3',
      actor: '@data_monk',
      actorType: 'HUMAN',
      type: 'COLLAB',
      text: 'Invited you to collaborate on "Token Attribution Drift" report.',
      context: 'shared report + charts',
      time: '15m',
    },
    {
      id: 'n4',
      actor: 'Vision-Agent-R2',
      actorType: 'AI',
      type: 'RESULT',
      text: 'Finished training run #A129. Validation improved from 91.8% to 93.0%.',
      context: 'experiment saved with full logs',
      time: '21m',
    },
    {
      id: 'n5',
      actor: '@quant_girl',
      actorType: 'HUMAN',
      type: 'COMMENT',
      text: 'Left feedback on your report graph legends for better reproducibility.',
      context: 'on public research feed',
      time: '43m',
    },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', zIndex: 1 }}>

      {/* ══ TOPBAR ══ */}
      <div style={{ height: '52px', display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--b0)', background: 'var(--deep)', flexShrink: 0, zIndex: 20 }}>
        <div style={{ width: sidebarOpen ? '240px' : '58px', display: 'flex', alignItems: 'center', gap: '11px', padding: '0 16px', borderRight: '1px solid var(--b0)', overflow: 'hidden', transition: 'width .25s', flexShrink: 0, position: 'relative' }}>
          <div style={{ width: '30px', height: '30px', border: '1.5px solid var(--cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', flexShrink: 0, borderRadius: '3px' }}>
            <div style={{ position: 'absolute', width: '100%', height: '2px', background: 'var(--cyan)', opacity: .5, animation: 'scanLine 2.5s linear infinite', top: 0 }} />
            <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '19px', color: 'var(--cyan)' }}>X</span>
          </div>
          {sidebarOpen && <div><div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '18px', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>JOR<span style={{ color: 'var(--cyan)' }}>VEX</span></div><div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.13em' }}> {'>/'}AI RESEARCH ENGINE</div> </div>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            style={{
              position: 'absolute',
              right: 4,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--t3)',
              fontSize: '12px',
              lineHeight: 1,
              padding: 0,
            }}
          >
            {sidebarOpen ? '◂' : '▸'}
          </button>

        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 18px', flex: 1 }}>
          <span style={{ color: 'var(--t3)', fontSize: '11px', cursor: 'pointer' }} onClick={() => setViewProfile(false)}>LAB</span>
          {viewProfile
            ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--cyan)', fontSize: '12px' }}>PROFILE</span></>
            : isHomeActive
              ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--t1)', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{homeMissionTitle}</span></>
              : isAutoEDAHubActive
                ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--cyan)', fontSize: '12px', fontWeight: 600 }}>AUTO EDA</span></>
              : isDocumentAnalysisActive
                ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--violet)', fontSize: '12px', fontWeight: 600 }}>DOCUMENT ANALYSIS</span></>
              : isMLExperimentsHubActive
                ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--orange)', fontSize: '12px', fontWeight: 600 }}>ML EXPERIMENTS</span></>
              : isDLExperimentsHubActive
                ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--green)', fontSize: '12px', fontWeight: 600 }}>DL EXPERIMENTS</span></>
              : isLocalAgentsActive
                ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--green)', fontSize: '12px', fontWeight: 600 }}>AI AGENTS [LOCAL]</span></>
              : isLabsHubActive
                ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--violet)', fontSize: '12px', fontWeight: 600 }}>LABS DIRECTORY</span></>
                : isDemosHubActive
                  ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--gold)', fontSize: '12px', fontWeight: 600 }}>DEMOS LIBRARY</span></>
                : isLabModuleActive
                  ? <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--violet)', fontSize: '12px', fontWeight: 600 }}>LABS</span><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: mod?.color, fontSize: '12px', fontWeight: 600 }}>{mod?.cat}</span><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: 'var(--t1)', fontSize: '12px', fontWeight: 500 }}>{mod?.label}</span></>
              : <><span style={{ color: 'var(--t3)' }}>/</span><span style={{ color: mod?.color, fontSize: '14px' }}>{mod?.icon}</span><span style={{ color: 'var(--t1)', fontSize: '13px', fontWeight: 500 }}>{mod?.label}</span></>
          }
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '0 16px' }}>
          {/* <button onClick={toggleHelpPanel} style={{ padding: '6px 13px', border: `1px solid ${helpOpen ? 'var(--green)' : 'var(--b1)'}`, background: helpOpen ? 'var(--ggreen)' : 'transparent', color: helpOpen ? 'var(--green)' : 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', letterSpacing: '.08em', transition: 'all .15s' }}>? GUIDE</button> */}
          
          {/* <button onClick={toggleNotifPanel} style={{ padding: '6px 12px', border: `1px solid ${notifOpen ? 'var(--violet)' : 'var(--b1)'}`, background: notifOpen ? 'var(--gviolet)' : 'transparent', color: notifOpen ? 'var(--violet)' : 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', letterSpacing: '.08em', transition: 'all .15s' }}>◉ NOTIFICATIONS</button> */}
          
          {/* <button onClick={() => setInstructor(!instructor)} style={{ padding: '6px 10px', border: `1px solid ${instructor ? 'var(--gold)' : 'var(--b0)'}`, background: instructor ? 'var(--ggold)' : 'transparent', color: instructor ? 'var(--gold)' : 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>INSTR</button> */}
          
          <div style={{ width: '1px', height: '22px', background: 'var(--b1)' }} />

          <PaletteSwitcher />

          <button
            onClick={() => setUsageOpen(true)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--b1)',
              background: 'transparent',
              color: 'var(--t2)',
              fontSize: '10px',
              cursor: 'pointer',
              borderRadius: '2px',
              fontFamily: 'JetBrains Mono',
              letterSpacing: '.10em',
              fontWeight: 700,
            }}
            title="Usage and limits"
          >
            MY USAGE
          </button>

          <WorkspaceDock
            isAuthenticated={isAuthenticated}
            isPremium={isPremiumAccount}
            onAuthOpen={openAuth}
            onRestore={handleWorkspaceRestore}
          />

          <button
            onClick={openPricingPage}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--cyan)44',
              background: 'var(--gcyan)',
              color: 'var(--cyan)',
              fontSize: '10px',
              cursor: 'pointer',
              borderRadius: '2px',
              fontFamily: 'JetBrains Mono',
              letterSpacing: '.10em',
              fontWeight: 700,
            }}
          >
            PRICING
          </button>

          {isAuthenticated
            ? <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setViewProfile(true)}>
              <div style={{ width: '28px', height: '28px', borderRadius: '2px', background: 'var(--gcyan)', border: '1px solid var(--cyan)44', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'var(--cyan)', fontWeight: 700, fontFamily: 'Syne' }}>{authDisplayName.slice(0, 2).toUpperCase()}</div>
              <div><div style={{ fontSize: '10px', color: 'var(--t1)', fontWeight: 600 }}>{authDisplayName}</div><div style={{ fontSize: '9px', color: 'var(--t3)' }}>{authMeta}</div></div>
              <button onClick={e => { e.stopPropagation(); confirmLogout(); }} style={{ background: 'none', border: 'none', color: 'var(--t3)', cursor: 'pointer', fontSize: '10px', fontFamily: 'JetBrains Mono' }}>logout</button>
            </div>
            : <>
              <button onClick={() => openAuth('login')} style={{ padding: '6px 15px', border: '1px solid var(--cyan)44', background: 'var(--gcyan)', color: 'var(--cyan)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', letterSpacing: '.08em', fontWeight: 600 }}>LOGIN ↗</button>
            </>
          }
          <div style={{ color: 'var(--t3)', fontSize: '10px', letterSpacing: '.06em', minWidth: '72px' }}>{time.toLocaleTimeString('en', { hour12: false })}</div>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--green)', animation: 'pulse 2s infinite' }} />
        </div>
      </div>

      {authOpen && <AuthModal onClose={()=>setAuthOpen(false)} defaultView={authView}/>}
      {usageOpen && <UsageModal onClose={() => setUsageOpen(false)} />}

      {/* ── Proactive upgrade nudge (free tier users) ── */}
      {isAuthenticated && !isPremiumAccount && (
        <UpgradeNudge
          usageSummary={usageSummary}
          isPremium={isPremiumAccount}
          onUpgrade={openUpgradeFromLimit}
        />
      )}
      {membershipOpen && (
        <MembershipModal
          onClose={() => setMembershipOpen(false)}
          onAuthOpen={openAuth}
          defaultFocus={membershipFocus}
        />
      )}
      {checkoutRecovery && (
        <CheckoutSuccessModal
          plan={checkoutRecovery.plan}
          syncStatus={checkoutRecovery.status}
          onRetry={() => setCheckoutRecovery((current) => (current ? { ...current, status: 'syncing', retryNonce: (current.retryNonce || 0) + 1 } : current))}
          onClose={() => {
            if (checkoutRecovery.status === 'ready') clearPendingCheckout();
            setCheckoutRecovery(null);
          }}
        />
      )}
      {logoutConfirmOpen && <LogoutConfirmModal onCancel={() => setLogoutConfirmOpen(false)} onConfirm={handleLogoutConfirmed} />}


      {/* ══ BODY ══ */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── SIDEBAR ── */}
        <div style={{ width: sidebarOpen ? '240px' : '58px', flexShrink: 0, borderRight: '1px solid var(--b0)', background: 'var(--deep)', display: 'flex', flexDirection: 'column', transition: 'width .25s', overflow: 'hidden' }}>
          {sidebarOpen && SIDEBAR_MODULES.length > 6 && (
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--b0)' }}>
              <input
                value={sidebarSearch}
                onChange={(e) => setSidebarSearch(e.target.value)}
                placeholder="Search modules..."
                style={{
                  width: '100%',
                  padding: '7px 10px',
                  background: 'var(--surface)',
                  border: '1px solid var(--b1)',
                  color: 'var(--t1)',
                  fontSize: '10px',
                  borderRadius: '2px',
                  fontFamily: 'JetBrains Mono',
                  outline: 'none',
                }}
              />
              {sidebarQuery && (
                <div style={{ marginTop: '6px', fontSize: '9px', color: 'var(--t3)', letterSpacing: '.08em' }}>
                  {filteredModules.length} RESULT{filteredModules.length === 1 ? '' : 'S'}
                </div>
              )}
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filteredModules.length === 0 && sidebarOpen && (
              <div style={{ padding: '14px', fontSize: '10px', color: 'var(--t3)' }}>
                No modules match your search.
              </div>
            )}
            {visibleCats.map(cat => {
              // Sidebar categories are fixed for now: no expand/collapse.
              const catExpanded = true;
              return (
                <div key={cat}>
                  {/* <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: sidebarOpen ? '10px 14px 4px' : '8px 0', cursor: 'default' }}
                  >
                    {sidebarOpen && <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.14em', textTransform: 'uppercase' }}>{cat}</div>}
                  </div> */}

                  {catExpanded && filteredModules.filter(m => m.cat === cat).map(m => {
                    const isActive =
                      !viewProfile &&
                      ((m.id === 'labs' && (isLabsHubActive || isLabModuleActive)) ||
                        activeModule === m.id);
                    return (
                      <div key={m.id}>
                        <div onClick={() => {
                          if (m.id === 'labs') {
                            openLabsHub(labsFocusCategory);
                          } else {
                            navigateModule(m.id);
                          }
                        }}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: sidebarOpen ? '10px 14px' : '12px 0', justifyContent: sidebarOpen ? 'flex-start' : 'center', cursor: 'pointer', background: isActive ? alpha(m.color, 12) : 'transparent', borderRight: `2px solid ${isActive ? m.color : 'transparent'}`, transition: 'all .12s' }}
                          onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--hover)'; }}
                          onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}> 

                          <div
                            style={{
                              width: '22px',
                              height: '22px',
                              minWidth: '22px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '4px',
                              flexShrink: 0,
                              color: isActive ? m.color : 'var(--t3)',
                              background: isActive ? alpha(m.color, 14) : 'transparent',
                              fontSize: '14px',
                              lineHeight: 1,
                              textAlign: 'center',
                            }}
                          >
                            {m.icon}
                          </div>

                          {sidebarOpen && <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: isActive ? 600 : 400, color: isActive ? m.color : 'var(--t1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</div>
                            <div style={{ fontSize: '9px', color: 'var(--t3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.sub}</div>
                          </div>}
                        </div>

                        {/* subsections removed — no expansion of module subsections */}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {sidebarOpen && (
            <div style={{ padding: '12px 14px', borderTop: '1px solid var(--b0)' }}>
              {[['ENGINE', 'ACTIVE', 'var(--green)'], ['BACKEND', 'BROWSER', 'var(--gold)']].map(([k, v, c]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span style={{ fontSize: '9px', color: 'var(--t3)' }}>{k}</span>
                  <span style={{ fontSize: '9px', color: c }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Sidebar ↔ Main collapse handle ── */}
        {!isMobileViewport && (
          <div style={{ width: 0, position: 'relative', flexShrink: 0, zIndex: 50 }}>
            <button
              onClick={() => setSidebarOpen(p => !p)}
              title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 22px rgba(34,211,238,.45), 0 2px 12px rgba(0,0,0,.7)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,.75)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 12px rgba(34,211,238,.18), 0 2px 10px rgba(0,0,0,.6)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,.38)'; }}
              style={{
                position: 'absolute',
                left: -11,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 22, height: 46,
                background: 'rgba(6,11,20,.96)',
                border: '1px solid rgba(34,211,238,.38)',
                borderRadius: 11,
                color: '#22d3ee',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                fontSize: 13, fontWeight: 800, fontFamily: 'JetBrains Mono',
                boxShadow: '0 0 12px rgba(34,211,238,.18), 0 2px 10px rgba(0,0,0,.6)',
                transition: 'box-shadow .15s, border-color .15s',
                padding: 0, lineHeight: 1, userSelect: 'none',
              }}
            >
              {sidebarOpen ? '‹' : '›'}
            </button>
          </div>
        )}

        {/* ── MAIN ── */}
        <div style={{ flex: 1, overflowY: isHomeActive ? 'hidden' : 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {!viewProfile && isLabModuleActive && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', flexShrink: 0 }}>
              <button
                onClick={() => openLabsHub(mod?.cat)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '999px',
                  border: `1px solid ${alpha(mod?.color || 'var(--violet)', 55)}`,
                  background: alpha(mod?.color || 'var(--violet)', 15),
                  color: mod?.color || 'var(--violet)',
                  fontSize: '10px',
                  cursor: 'pointer',
                  fontFamily: 'JetBrains Mono',
                  letterSpacing: '.1em',
                  fontWeight: 700,
                }}
              >
                ← BACK TO {mod?.cat}
              </button>
              <div style={{ fontSize: '10px', color: 'var(--t3)', letterSpacing: '.1em' }}>
                LAB DIRECTORY / {mod?.cat} / {mod?.label}
              </div>
            </div>
          )}
          {!viewProfile && isLabModuleActive && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '46px', height: '46px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: alpha(mod?.color, 12), border: `1px solid ${alpha(mod?.color, 44)}`, borderRadius: '3px', fontSize: '22px', color: mod?.color }}>{mod?.icon}</div>
                <div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '19px' }}>{mod?.label}</div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)', letterSpacing: '.1em' }}>{mod?.sub}</div>
                </div>
              </div>
            </div>
          )}
          {instructor && (
            <div style={{ padding: '10px 16px', background: 'var(--ggold)', border: '1px solid var(--gold)33', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              <span style={{ fontSize: '10px', color: 'var(--gold)', letterSpacing: '.1em', fontWeight: 600 }}>⬡ INSTRUCTOR CONSOLE</span>
              {['Lock Module', 'View Student Runs', 'Export Metrics', 'Reset Sessions'].map(a => (
                <button key={a} style={{ padding: '4px 10px', background: 'var(--raised)', border: '1px solid var(--b0)', color: 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>{a}</button>
              ))}
            </div>
          )}
          {renderModule && (
            <ErrorBoundary key={activeModule} onReset={() => {}}>
              {renderModule()}
            </ErrorBoundary>
          )}
        </div>

        {/* ── NOTIFICATIONS PANEL ── */}
        {notifOpen && (
          <div style={{ width: '300px', flexShrink: 0, borderLeft: '1px solid var(--b0)', background: 'var(--deep)', display: 'flex', flexDirection: 'column', animation: 'slideInRight .2s ease', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--b0)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '3px' }}>NOTIFICATIONS</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '14px' }}>Research Activity Feed</div>
              </div>
              <button onClick={() => setNotifOpen(false)} style={{ background: 'var(--raised)', border: '1px solid var(--b0)', color: 'var(--t2)', cursor: 'pointer', width: '26px', height: '26px', borderRadius: '2px', fontSize: '11px' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
              {notifications.map((n) => {
                const isAI = n.actorType === 'AI';
                const accent = isAI ? 'var(--violet)' : 'var(--green)';
                return (
                  <div key={n.id} style={{ borderBottom: '1px solid var(--b0)', padding: '10px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                        <span style={{ fontSize: '10px', color: accent, fontWeight: 700, whiteSpace: 'nowrap' }}>{n.actor}</span>
                        <span style={{ fontSize: '8px', color: accent, border: `1px solid ${accent}55`, background: `${accent}15`, borderRadius: '2px', padding: '1px 5px', letterSpacing: '.08em' }}>{n.actorType}</span>
                        <span style={{ fontSize: '8px', color: 'var(--t3)', letterSpacing: '.08em' }}>{n.type}</span>
                      </div>
                      <span style={{ fontSize: '9px', color: 'var(--t3)', flexShrink: 0 }}>{n.time}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--t1)', lineHeight: 1.5, marginBottom: '4px' }}>{n.text}</div>
                    <div style={{ fontSize: '10px', color: 'var(--t3)' }}>{n.context}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── HELP PANEL ── */}
        {helpOpen && (
          <div style={{ width: '272px', flexShrink: 0, borderLeft: '1px solid var(--b0)', background: 'var(--deep)', display: 'flex', flexDirection: 'column', animation: 'slideInRight .2s ease', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--b0)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '3px' }}>GUIDE</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '14px' }}>{mod?.label}</div>
              </div>
              <button onClick={() => setHelpOpen(false)} style={{ background: 'var(--raised)', border: '1px solid var(--b0)', color: 'var(--t2)', cursor: 'pointer', width: '26px', height: '26px', borderRadius: '2px', fontSize: '11px' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              {guide.map(([icon, label, desc]) => (
                <div key={label} style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                  <span style={{ color: mod?.color || 'var(--cyan)', fontSize: '17px', lineHeight: 1.2, flexShrink: 0 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--t1)', fontWeight: 600, marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '11px', color: 'var(--t2)', lineHeight: 1.55 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ══ STATUS BAR ══ */}
      <div style={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', background: 'var(--void)', borderTop: '1px solid var(--b0)', flexShrink: 0 }}>
        <div style={{ display: 'flex' }}>
          {['v2.1.0',  'Your AI Research Workstation', '100% On Browser = 100% Privacy', 'No Data Uploaded to the server', 'Local AI Agents', 'Auto AI Research', 'Tamper-proof research Powered by Polygon Blockchain',].map((s, i) => (
            <span key={i} style={{ fontSize: '10px', color: 'var(--t3)', letterSpacing: '.05em' }}>
              {i > 0 && <span style={{ margin: '0 14px', color: 'var(--b1)' }}>|</span>}{s}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '14px' }}>
          <span style={{ fontSize: '10px', color: 'var(--t3)' }}>© 2026 Jorvex XYZ</span>
        </div>
      </div>
      <style>{`
        @keyframes ddCheckoutConfetti {
          0% { transform: translate3d(0, -18px, 0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate3d(20px, 430px, 0) rotate(280deg); opacity: 0; }
        }
      `}</style>
      <ChatSystem />
    </div>
  );
}
